import React, { useEffect, useState } from 'react';
import { ModalPortal } from '../../../modal/modal-portal';
import {
  scrimMotion,
  StateSwitch,
  StatusLabel,
  useOverlayMotion,
} from '../../../../motion';
import { getShiftDetail, resolveShift } from '../../../../api/shifts-client';
import toast from '../../../../utils/toast';
import { useAuth } from '../../../../context/auth-provider';
import { can, PERMISSIONS } from '../../../../auth/permissions';
import {
  FieldLabel,
  PrimaryAction,
  ResolvePanel,
  TimeField,
} from '../../../shifts/shifts.styled';
import {
  ShiftDetailResponse,
  ShiftHistoryRow,
  ShiftLogEntry,
} from '../../../../interfaces/shift-interfaces';
import { Handoff } from '../../../../interfaces/handoff-interfaces';
import { IqHandoffCard } from '../../../iq/iq-handoff-card';
import {
  Clock,
  clockDayTime,
  clockNote,
  clockTime,
  toZonedInputValue,
  zonedInputToIso,
} from '../../../../function-utils/building-time';
import { useClock } from '../../../../context/clock-context';
import { BothClocks, ClockNote } from '../../../clock-note/clock-note';
import { useDialogFocus } from '../../../hooks/use-dialog-focus';
import {
  Caveat,
  CloseButton,
  CountGrid,
  CountLabel,
  CountTile,
  CountValue,
  Drawer,
  DrawerHead,
  DrawerStack,
  DrawerSub,
  DrawerTitle,
  EntryBody,
  EntryHead,
  EntryItem,
  EntryKind,
  EntryList,
  EntryTime,
  ErrorPanel,
  Fact,
  FactGrid,
  FactLabel,
  FactValue,
  HeadActions,
  Scrim,
  Section,
  SectionTitle,
  SkeletonRow,
  WarnBand,
} from './dashboard-shifts.styled';

/**
 * WHAT HAPPENED ON THIS SHIFT.
 *
 * ════════════ IT SHOWS TWO DIFFERENT KINDS OF THING, AND SAYS WHICH ═════════
 * The handover at the top is a MODEL-WRITTEN, FROZEN record — it wears the
 * aurora because <IqHandoffCard> carries it, and it is the same object, from the
 * same server-side renderer, that the concierge who received it reads. Two
 * renderings of one chain-of-custody record is how a supervisor ends up quoting
 * wording the desk never saw, so this component does not re-word any of it.
 *
 * Everything below the handover is the LOG: rows a person typed, plus counts
 * from read-model.js's single aggregate. No aurora on any of it, because none of
 * it was written by a model.
 *
 * ════════════ NO SUMMARY IS GENERATED HERE, EVER ════════════
 * Owner decision, 2026-08-11. A shift that ended with a plain clock-out shows
 * its raw activity and says no handover was written. There is no "summarise
 * this" affordance and there must not be one: a manager scrolling a month of
 * history behind a billed vendor call is the access pattern that empties an IQ
 * budget, and a summary attributed to a shift whose worker never saw it is the
 * opposite of what a handover is.
 */

const COPY = {
  loading: 'Reading the shift…',
  failed: 'Could not read that shift.',
  close: 'Close',
  expand: 'Full screen',
  collapse: 'Exit full screen',

  // ── CLOSING SOMEBODY ELSE'S SHIFT ──
  // Owner decision, 2026-08-11. The desk's own card no longer offers "Forgot to
  // clock out?": choosing the time a colleague's shift ended is a supervisory
  // act, and it belongs where a supervisor already is. The endpoint is the same
  // one the desk used (POST /shifts/:id/resolve); the difference is that
  // resolving ANOTHER person's shift requires shift:manage server-side, so the
  // control is gated on the same permission here rather than offered and
  // refused.
  closeShiftTitle: 'This shift is still running',
  closeShiftNote:
    'A shift that was never clocked out blocks that person from clocking in at this building again. Give the time it actually ended — it is recorded as a correction, with your name against it.',
  endedAt: 'Ended at',
  closeShiftAction: 'Close this shift',
  closeShiftBusy: 'Closing…',
  closedOk: 'Shift closed',
  badTime: 'Enter the time the shift ended.',
  shift: 'The shift',
  handover: 'The handover',
  noHandover: 'No handover was written',
  noHandoverNote:
    'This shift ended with a plain clock-out, so there is no written summary. Everything recorded during it is below.',
  activity: 'What was logged',
  incidents: 'Flagged',
  counts: 'Totals',
  emptyLog: 'Nothing was logged during this shift.',
  truncated:
    'This is the first part of the window. There were more entries than fit on one page — open Building Activities for the full record.',
  clamped:
    'The clock ran longer than a shift can be summarised. What is below covers only the most recent 26 hours of it, not the whole open period.',
  retro: 'Closed after the fact, not clocked out live.',
  stillOpen: 'Still on the clock — this is what has happened so far.',
  droppedAlarm:
    'Some entries were classified inconsistently between the database and the app. The list below may be incomplete; this is worth reporting.',
};

const minutesLabel = (mins: number): string => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (!h) return `${m}m`;

  return m ? `${h}h ${m}m` : `${h}h`;
};

// The count keys read-model.js returns, in the order a supervisor scans them,
// with the labels this product uses for them. Anything the server adds later is
// simply not shown rather than rendered under its raw column name — a tile
// labelled `people_admitted` is worse than no tile.
const COUNT_TILES: { key: string; label: string }[] = [
  { key: 'entries', label: 'Log entries' },
  { key: 'incidents', label: 'Flagged' },
  { key: 'visitor_entries', label: 'Visitors' },
  { key: 'people_admitted', label: 'Admitted' },
  { key: 'packages_logged', label: 'Parcels in' },
  { key: 'packages_picked_up', label: 'Parcels out' },
  { key: 'packages_pending', label: 'On the shelf' },
];

const toneOf = (e: ShiftLogEntry): 'alert' | 'warning' | 'neutral' => {
  if (e.severity === 'incident') return 'alert';
  if (e.severity === 'follow_up') return 'warning';

  return 'neutral';
};

const EntryRow: React.FC<{ entry: ShiftLogEntry; tz: string }> = ({
  entry,
  tz,
}) => (
  <EntryItem tone={toneOf(entry)}>
    <EntryHead>
      <EntryTime>{clockTime(entry.at, tz)}</EntryTime>
      <EntryKind>{entry.kind}</EntryKind>
      {entry.location ? <EntryTime>{entry.location}</EntryTime> : null}
    </EntryHead>
    {/* `people` holds REAL names — this endpoint requires the same log-read pair
        the daily-log screen is gated on, so showing them here discloses nothing
        the reader could not already open. */}
    {entry.people && entry.people.length ? (
      <EntryBody>{entry.people.join(', ')}</EntryBody>
    ) : null}
    {entry.text && entry.text.length ? (
      <EntryBody>{entry.text.join(' — ')}</EntryBody>
    ) : null}
  </EntryItem>
);

/**
 * The supervisor's close-a-running-shift panel.
 *
 * Seeded with NOW, because "I have just noticed this is still open" is the
 * overwhelmingly common case and now is usually close enough to right. The
 * `max` is a courtesy, NOT the enforcement: the server refuses a future end
 * time and an end before the start outright (shifts.js), and it says so in a
 * sentence worth showing, which is why those errors are surfaced verbatim.
 */
const CloseShiftPanel: React.FC<{
  shift: ShiftHistoryRow;
  /** THE TYPIST'S CLOCK (R-T12, WP-B9): a manager closing a shift from the
   *  dashboard types the end time on THEIR OWN clock; at a desk it would be the
   *  building's. The field is read on it and labelled with it. */
  clock: Clock;
  onClosed: () => void;
}> = ({ shift, clock, onClosed }) => {
  const [value, setValue] = useState(() => toZonedInputValue(new Date(), clock.zone));
  const [busy, setBusy] = useState(false);
  const nowMax = toZonedInputValue(new Date(), clock.zone);

  const submit = async () => {
    // A datetime-local has no zone: it is read as the wall clock of the
    // typist's clock and turned into the instant the server validates.
    const iso = zonedInputToIso(value, clock.zone);
    if (!iso) {
      toast.error(COPY.badTime);

      return;
    }

    setBusy(true);
    try {
      await resolveShift(shift.id, iso);
      toast.success(COPY.closedOk);
      onClosed();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : COPY.closeShiftAction);
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResolvePanel>
      <SectionTitle>{COPY.closeShiftTitle}</SectionTitle>
      <Caveat>{COPY.closeShiftNote}</Caveat>
      <FieldLabel>
        {`${COPY.endedAt} (${clockNote(clock)})`}
        <TimeField
          type="datetime-local"
          value={value}
          max={nowMax}
          onChange={e => setValue(e.target.value)}
        />
      </FieldLabel>
      <PrimaryAction
        type="button"
        disabled={busy}
        onClick={() => {
          void submit();
        }}
      >
        {/* The label swaps without the button changing width. */}
        <StatusLabel
          state={busy ? 'busy' : 'idle'}
          labels={{ idle: COPY.closeShiftAction, busy: COPY.closeShiftBusy }}
        />
      </PrimaryAction>
    </ResolvePanel>
  );
};

interface Props {
  shift: ShiftHistoryRow;
  onClose: () => void;
  /** Re-read the list behind the drawer after a shift is closed from inside it. */
  onChanged?: () => void;
}

export const ShiftDetailDrawer: React.FC<Props> = ({
  shift,
  onClose,
  onChanged,
}) => {
  const { permissions } = useAuth();
  // The same permission the server checks for resolving somebody else's shift.
  // Offering the panel to anyone else would render a control whose only outcome
  // is a 403.
  const mayManageShifts = can(permissions, PERMISSIONS.SHIFT_MANAGE);
  const [data, setData] = useState<ShiftDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Per-open, not persisted: a reader who expanded one long handover has not
  // asked for every future shift to open full-bleed.
  const [fullscreen, setFullscreen] = useState(false);
  // Focus trap + Escape + focus restore, shared with every other dialog in the
  // dashboard rather than reimplemented here.
  const dialogRef = useDialogFocus<HTMLDivElement>(true, onClose);
  /* x + opacity from the right edge, and back out on close. NEVER width: the
     full-screen toggle must switch the width in one frame. */
  const drawerMotion = useOverlayMotion('drawer-right');

  useEffect(() => {
    let alive = true;
    setData(null);
    setError(null);
    getShiftDetail(shift.id)
      .then(res => {
        if (alive) setData(res);
      })
      .catch(e => {
        if (alive) setError(e instanceof Error ? e.message : COPY.failed);
      });

    return () => {
      alive = false;
    };
  }, [shift.id]);

  /* THE DRAWER'S CLOCK (R-T12): the surface's — on the manager's dashboard, the
     reader's own. The shift's BUILDING zone (`window.tz` / `shift.timezone`) is
     kept for the second line: "20:05 your time · 14:05 in Toronto". */
  const buildingZone = data?.window.tz || shift.timezone || '';
  const clock = useClock({ buildingId: shift.building_id, buildingZone });
  const tz = clock.zone;
  const entries = data?.entries || [];
  const incidents = data?.incidents || [];

  const body = (
    <>
      <Scrim {...scrimMotion} onClick={onClose} />
      <Drawer
        {...drawerMotion}
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Shift at ${shift.building_name}`}
        $fullscreen={fullscreen}
      >
        <DrawerHead>
          <div>
            <DrawerTitle>
              {shift.staff_name || 'Someone who no longer has an account'}
            </DrawerTitle>
            <DrawerSub>
              {`${shift.building_name} · ${clockDayTime(shift.started_at, tz)}`}
            </DrawerSub>
          </div>
          <HeadActions>
            {/* A handover is prose, and prose gets a reading width the 720px
                rail cannot always give it. aria-pressed rather than a swapped
                label: the control is one toggle, and a screen reader should
                hear the same name in both states. */}
            <CloseButton
              type="button"
              onClick={() => setFullscreen(v => !v)}
              aria-label={COPY.expand}
              aria-pressed={fullscreen}
              title={fullscreen ? COPY.collapse : COPY.expand}
            >
              {fullscreen ? '⤡' : '⤢'}
            </CloseButton>
            <CloseButton type="button" onClick={onClose} aria-label={COPY.close}>
              ✕
            </CloseButton>
          </HeadActions>
        </DrawerHead>

        {/* Skeleton → sections is a crossfade of one box, not a swap. */}
        <StateSwitch state={error ? 'error' : data ? 'ready' : 'loading'}>
        {error ? <ErrorPanel>{error}</ErrorPanel> : null}
        {!data && !error ? (
          <DrawerStack>
            <SkeletonRow />
            <SkeletonRow />
            <Caveat>{COPY.loading}</Caveat>
          </DrawerStack>
        ) : null}

        {data ? (
          <DrawerStack>
            <Section>
              <SectionTitle>{COPY.shift}</SectionTitle>
              <FactGrid>
                <Fact>
                  <FactLabel>Started</FactLabel>
                  <FactValue>
                    {clockDayTime(shift.started_at, tz)}
                    <BothClocks iso={shift.started_at} clock={clock} buildingZone={buildingZone} />
                  </FactValue>
                </Fact>
                <Fact>
                  <FactLabel>Ended</FactLabel>
                  <FactValue>
                    {shift.ended_at ? clockDayTime(shift.ended_at, tz) : '—'}
                    <BothClocks iso={shift.ended_at} clock={clock} buildingZone={buildingZone} />
                  </FactValue>
                </Fact>
                {/* NAME THE CLOCK, ONCE (WP-44, R-T12). Every timestamp in this
                    drawer — Started, Ended, and every entry in the log below —
                    is on one clock; this says whose. One statement rather than
                    a suffix on each value. */}
                <Fact>
                  <FactLabel>Times shown in</FactLabel>
                  <FactValue>
                    <ClockNote clock={clock} inline />
                  </FactValue>
                </Fact>
                <Fact>
                  <FactLabel>Length</FactLabel>
                  <FactValue>{minutesLabel(shift.duration_minutes)}</FactValue>
                </Fact>
                <Fact>
                  <FactLabel>Role</FactLabel>
                  <FactValue>{shift.staff_role_label || '—'}</FactValue>
                </Fact>
                {shift.portfolio_name ? (
                  <Fact>
                    <FactLabel>Portfolio</FactLabel>
                    <FactValue>{shift.portfolio_name}</FactValue>
                  </Fact>
                ) : null}
                {shift.ended_by_name ? (
                  <Fact>
                    <FactLabel>Closed by</FactLabel>
                    <FactValue>{shift.ended_by_name}</FactValue>
                  </Fact>
                ) : null}
              </FactGrid>
              {shift.still_open ? <Caveat>{COPY.stillOpen}</Caveat> : null}
              {shift.ended_retroactively ? <WarnBand>{COPY.retro}</WarnBand> : null}
              {data.window.clamped ? <WarnBand>{COPY.clamped}</WarnBand> : null}
            </Section>

            {/* Only for a shift that is still running, and only for somebody who
                may close it. A shift on the clock right now is indistinguishable
                from one forgotten last Tuesday until a person reads the times —
                so this is offered on both and worded for both. */}
            {shift.still_open && mayManageShifts ? (
              <CloseShiftPanel
                shift={shift}
                clock={clock}
                onClosed={() => {
                  onChanged?.();
                  onClose();
                }}
              />
            ) : null}

            {/* THE HANDOVER, UNTOUCHED. IqHandoffCard owns its own aurora and
                its own wording; this passes the server's object straight in.
                `observer` is the honest viewer role for a supervisor: they are
                neither the person who wrote it nor the one it was addressed to. */}
            {data.handoff ? (
              <IqHandoffCard
                handoff={data.handoff as Handoff}
                viewer="observer"
                buildingName={shift.building_name}
              />
            ) : (
              <Section>
                <SectionTitle>{COPY.handover}</SectionTitle>
                <FactValue>{COPY.noHandover}</FactValue>
                <Caveat>{COPY.noHandoverNote}</Caveat>
              </Section>
            )}

            {data.counts ? (
              <Section>
                <SectionTitle>{COPY.counts}</SectionTitle>
                <CountGrid>
                  {COUNT_TILES.filter(t => data.counts && t.key in data.counts).map(
                    t => (
                      <CountTile key={t.key}>
                        <CountValue>
                          {Number(
                            (data.counts as Record<string, number>)[t.key] || 0
                          )}
                        </CountValue>
                        <CountLabel>{t.label}</CountLabel>
                      </CountTile>
                    )
                  )}
                </CountGrid>
              </Section>
            ) : null}

            {incidents.length ? (
              <Section>
                <SectionTitle>{COPY.incidents}</SectionTitle>
                <EntryList>
                  {incidents.map(e => (
                    <EntryRow key={`i-${e.id}`} entry={e} tz={tz} />
                  ))}
                </EntryList>
                {/* A PREDICATE-DRIFT ALARM, surfaced rather than swallowed. The
                    server expects 0; anything else means SQL and JS disagree
                    about what an incident is, and the person looking at the
                    screen is the one who can report it. */}
                {data.incidents_dropped ? (
                  <WarnBand>{COPY.droppedAlarm}</WarnBand>
                ) : null}
              </Section>
            ) : null}

            <Section>
              <SectionTitle>{COPY.activity}</SectionTitle>
              {entries.length ? (
                <EntryList>
                  {entries.map(e => (
                    <EntryRow key={e.id} entry={e} tz={tz} />
                  ))}
                </EntryList>
              ) : (
                <Caveat>{COPY.emptyLog}</Caveat>
              )}
              {data.entries_truncated ? <WarnBand>{COPY.truncated}</WarnBand> : null}
            </Section>
          </DrawerStack>
        ) : null}
        </StateSwitch>
      </Drawer>
    </>
  );

  // PORTALLED to <body>. The drawer is fixed-position and the dashboard content
  // area is a transformed, scrolling container — a fixed child of a transformed
  // ancestor positions against THAT ancestor, not the viewport, which puts the
  // drawer halfway down the page. The exit animation still runs: the parent's
  // Presence reaches through the portal.
  return <ModalPortal>{body}</ModalPortal>;
};
